export type ProductLocale = 'ar' | 'en';

export interface ProductBrandingConfig {
  productCode: string;
  productName: string;
  productNameAr: string;
  logoUrl: string;
  faviconUrl: string;
  primaryColor: string;
  secondaryColor: string;
  backgroundStyle: string;
  loginTitleAr: string;
  loginTitleEn: string;
  loginSubtitleAr: string;
  loginSubtitleEn: string;
  loginSubmitAr: string;
  loginSubmitEn: string;
  loginSubmittingAr: string;
  loginSubmittingEn: string;
  loginCardTitleAr: string;
  loginCardTitleEn: string;
  loginCardSubtitleAr: string;
  loginCardSubtitleEn: string;
  heroDashboardLabelAr: string;
  heroDashboardLabelEn: string;
  rememberMeAr: string;
  rememberMeEn: string;
  benefitsAr: readonly string[];
  benefitsEn: readonly string[];
  supportUrl?: string;
  termsUrl?: string;
}

const WARID_LOGO_URL = '/branding/warid/logo.png';

const WARID_BRANDING: ProductBrandingConfig = {
  productCode: 'warid',
  productName: 'Wared POS',
  productNameAr: 'نظام وارد',
  logoUrl: WARID_LOGO_URL,
  faviconUrl: `${WARID_LOGO_URL}?v=2`,
  primaryColor: '#6B7CFF',
  secondaryColor: '#EEF0FF',
  backgroundStyle:
    'radial-gradient(900px 520px at 88% -8%, hsl(229 90% 70% / 0.05), transparent 62%), radial-gradient(800px 480px at -6% 8%, hsl(229 95% 78% / 0.05), transparent 62%), radial-gradient(700px 520px at 50% 55%, hsl(235 85% 72% / 0.05), transparent 65%), radial-gradient(900px 520px at 50% 110%, hsl(229 90% 70% / 0.05), transparent 62%), hsl(220 38% 97%)',
  loginTitleAr: 'تسجيل الدخول إلى نظام وارد',
  loginTitleEn: 'Sign in to Wared POS',
  loginSubtitleAr: 'أكمل تسجيل الدخول للوصول إلى نظام الكاشير وإدارة نشاطك التجاري.',
  loginSubtitleEn: 'Continue to your POS account and manage your business.',
  loginSubmitAr: 'تسجيل الدخول',
  loginSubmitEn: 'Sign in',
  loginSubmittingAr: 'جاري تسجيل الدخول...',
  loginSubmittingEn: 'Signing in...',
  loginCardTitleAr: 'مرحباً بعودتك',
  loginCardTitleEn: 'Welcome back',
  loginCardSubtitleAr: 'أدخل بيانات حسابك للمتابعة',
  loginCardSubtitleEn: 'Enter your account details to continue',
  heroDashboardLabelAr: 'لوحة التحكم',
  heroDashboardLabelEn: 'Dashboard',
  rememberMeAr: 'تذكرني',
  rememberMeEn: 'Remember me',
  benefitsAr: [
    'نظام كاشير سريع وسهل الاستخدام',
    'إدارة المبيعات والمخزون من مكان واحد',
    'تقارير فورية لنشاطك التجاري',
  ],
  benefitsEn: [
    'Fast, easy-to-use point of sale',
    'Manage sales and inventory in one place',
    'Real-time reports for your business',
  ],
  supportUrl: 'https://waredpos.com',
  termsUrl: 'https://waredpos.com',
};

/** Aliases that resolve to a canonical product branding entry. */
const PRODUCT_ALIASES: Record<string, string> = {
  warid: 'warid',
  'wared-pos': 'warid',
};

const PRODUCT_BRANDING: Record<string, ProductBrandingConfig> = {
  warid: WARID_BRANDING,
};

export function normalizeProductCode(productCode: string | null | undefined): string | null {
  if (!productCode?.trim()) return null;
  const normalized = productCode.trim().toLowerCase();
  return PRODUCT_ALIASES[normalized] ?? normalized;
}

export function getProductBranding(
  productCode: string | null | undefined,
  _locale: ProductLocale,
): ProductBrandingConfig | null {
  const canonical = normalizeProductCode(productCode);
  if (!canonical) return null;

  const branding = PRODUCT_BRANDING[canonical];
  if (!branding) return null;

  return branding;
}

export function getProductBrandingText(
  branding: ProductBrandingConfig,
  locale: ProductLocale,
): {
  title: string;
  subtitle: string;
  submit: string;
  submitting: string;
  cardTitle: string;
  cardSubtitle: string;
  rememberMe: string;
  benefits: readonly string[];
  productName: string;
  heroDashboardTitle: string;
} {
  const isAr = locale === 'ar';
  const productName = isAr ? branding.productNameAr : branding.productName;
  const dashboardLabel = isAr ? branding.heroDashboardLabelAr : branding.heroDashboardLabelEn;

  return {
    title: isAr ? branding.loginTitleAr : branding.loginTitleEn,
    subtitle: isAr ? branding.loginSubtitleAr : branding.loginSubtitleEn,
    submit: isAr ? branding.loginSubmitAr : branding.loginSubmitEn,
    submitting: isAr ? branding.loginSubmittingAr : branding.loginSubmittingEn,
    cardTitle: isAr ? branding.loginCardTitleAr : branding.loginCardTitleEn,
    cardSubtitle: isAr ? branding.loginCardSubtitleAr : branding.loginCardSubtitleEn,
    rememberMe: isAr ? branding.rememberMeAr : branding.rememberMeEn,
    benefits: isAr ? branding.benefitsAr : branding.benefitsEn,
    productName,
    heroDashboardTitle: `${dashboardLabel} — ${productName}`,
  };
}

/** Convert #RRGGBB to HSL components for CSS variables (h s% l%). */
export function hexToHslComponents(hex: string): string {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return '233 100% 71%';

  const r = parseInt(normalized.slice(0, 2), 16) / 255;
  const g = parseInt(normalized.slice(2, 4), 16) / 255;
  const b = parseInt(normalized.slice(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) return `0 0% ${Math.round(l * 100)}%`;

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
